import React from 'react';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { Radio } from '@atlaskit/radio';
import { FormValues, ChampionPreference } from '../../../types/ChampionApplicationFormTypes';

type FieldErrors = {
  championPreference?: string;
};

type Props = {
  values: FormValues;
  updateField: <K extends keyof FormValues>(key: K, value: FormValues[K]) => void;
  errors?: FieldErrors;
};

const PREFERENCE_OPTIONS: {
  id: NonNullable<ChampionPreference>;
  label: string;
  group: 'community' | 'success';
}[] = [
  {
    id: 'community_forums_contributor',
    label: 'Contributing to Atlassian Community Forums',
    group: 'community',
  },
  {
    id: 'community_event_organizer',
    label: 'Organizing Atlassian Community Events',
    group: 'community',
  },
  {
    id: 'community_content_creator',
    label: 'Creating original thought leadership or learning content for social media',
    group: 'community',
  },
  {
    id: 'success_training_builder',
    label: 'Creating or delivering Atlassian training for your organization\u2019s end users',
    group: 'success',
  },
  {
    id: 'success_cug_leader',
    label: 'Running Atlassian-focused training events or a Company User Group in your organization',
    group: 'success',
  },
  {
    id: 'success_coe_contributor',
    label: 'Contributing Atlassian knowledge to a Center of Excellence in your organization',
    group: 'success',
  },
];

export const PreferenceSection: React.FC<Props> = ({ values, updateField, errors }) => {
  const selected = values.championPreference;

  const handleChange = (id: ChampionPreference) => () => updateField('championPreference', id);

  const communityOptions = PREFERENCE_OPTIONS.filter((o) => o.group === 'community');
  const successOptions = PREFERENCE_OPTIONS.filter((o) => o.group === 'success');

  return (
    <fieldset css={sectionBoxStyles}>
      <legend css={legendLabelStyles}>
        <span css={requiredStyles}>*</span>{' '}
        How do you want to contribute as an Atlassian Champion? Select your top preference
      </legend>

      <div css={controlTopStyles}>
        <p css={groupTitleStyles}>Community Champion</p>
        {communityOptions.map((opt) => (
          <Radio
            key={opt.id}
            name="champion_preference"
            label={opt.label}
            value={opt.id}
            isChecked={selected === opt.id}
            onChange={handleChange(opt.id)}
          />
        ))}

        <p css={groupTitleTopStyles}>
          Success Champion (join waitlist)
        </p>
        {successOptions.map((opt) => (
          <Radio
            key={opt.id}
            name="champion_preference"
            label={opt.label}
            value={opt.id}
            isChecked={selected === opt.id}
            onChange={handleChange(opt.id)}
          />
        ))}
      </div>

      {errors?.championPreference && <div css={errorStyles}>{errors.championPreference}</div>}
    </fieldset>
  );
};

const sectionBoxStyles = css({
  border: `1px solid ${token('color.border')}`,
  borderRadius: '4px',
  padding: token('space.300'),
  marginBottom: token('space.400'),
  backgroundColor: token('elevation.surface'),
});

const legendLabelStyles = css({
  fontWeight: 600,
  fontSize: '14px',
});

const controlTopStyles = css({
  marginTop: token('space.100'),
});

const groupTitleStyles = css({
  fontWeight: 600,
  marginBottom: token('space.050'),
});

const groupTitleTopStyles = css({
  fontWeight: 600,
  marginTop: token('space.200'),
  marginBottom: token('space.050'),
});

const requiredStyles = css({
  color: token('color.text.danger'),
});

const errorStyles = css({
  color: token('color.text.danger'),
  fontSize: '12px',
  marginTop: token('space.050'),
});
